import { useEffect, useRef, RefObject } from 'react'

/**
 * Provides horizontal scroll behavior for touch and mouse events
 * How: A container reference is returned which listens to mouse and touch events.
 */
const useHorizontalScroll = (): RefObject<HTMLDivElement> => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (container === null) return

    container.addEventListener('mousedown', event => handleMouseDown(container, event))
    container.addEventListener('touchstart', event => handleMouseDown(container, event), { passive: false })

    return () => {
      container.removeEventListener('mousedown', event => handleMouseDown(container, event))
      container.removeEventListener('touchstart', event => handleMouseDown(container, event))
    }
  }, [])

  /**
   * this hook prevents hides the scroll bar
   */
  useEffect(() => {
    const container = containerRef.current

    if (container) {
      container.style.overflow = 'hidden'
      container.style.paddingBottom = '1px'
    }
  }, [])

  return containerRef
}

/**
 * Calculate the starting x-position of the mouse or touch event
 * @param event
 * @returns
 */
const getEventXPosition = (event: MouseEvent | TouchEvent): number => {
  if (event instanceof MouseEvent) {
    return event.pageX
  } else if (event.touches.length > 0) {
    return event.touches[0].pageX
  }
  return 0
}

/**
 * handle the scrolling behavior by calculating the event's x position and listing to touch/mouse events
 */
const handleMouseDown = (container: HTMLDivElement, event: MouseEvent | TouchEvent): void => {
  const startX = getEventXPosition(event)
  const scrollLeft = container.scrollLeft

  const handleMouseMove = (event: MouseEvent | TouchEvent): void => {
    const distance = getEventXPosition(event) - startX
    container.scrollLeft = scrollLeft - distance
  }

  const handleMouseUp = (): void => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('touchmove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('touchend', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('touchmove', handleMouseMove, { passive: false })
  document.addEventListener('mouseup', handleMouseUp)
  document.addEventListener('touchend', handleMouseUp)
}

export default useHorizontalScroll
